import * as React from 'react';
import styled from 'styled-components';

import { ActivitiesGrid } from '@process-advisor/process-miner/filters/activities/activityGrid';
import { SingleBlock } from '@process-advisor/process-miner/slicers/singleBlock';
import { Section } from '@process-advisor/process-miner/slicers/section';
import { Button } from '@process-advisor/process-miner/components/shared/button';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { getShadow, getZIndex } from '@process-advisor/process-miner/shared/theme';
import { AlertMessage } from '@process-advisor/process-miner/components/alerts/alert';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { Dropdown } from '@process-advisor/process-miner/filters/dropdown';
import { NumberInput } from '@process-advisor/process-miner/modals/numberInput';
import {
    getColor,
    getFontFamily,
    getFontSize,
    getLetterSpacing,
    getLineHeight,
} from '@process-advisor/process-miner/shared/theme';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    box-shadow: ${getShadow('pane')};
    z-index: ${getZIndex('panel')};
    padding-bottom: 5px;
`;

const GridOptions = styled.div`
    width: 100%;
`;

const RowRetention = styled.div`
  display: grid;
  grid-template-columns: 100px 200px;
`;

const RowConstraints= styled.div`
  display: grid;
  padding-top: 5px;
  grid-template-columns: min-content; 
`;

const RowApply = styled.div`
  display: grid;
  justify-content: center;
  padding-top: 10px;
`;

const HelpText = styled.div`
    font-size: 12px;
    color: #6b778c;
    font-style: italic;
    padding: 2px 12px 8px;
    text-align: center;
    width: 100%;
    flex-basis: 100%;  // Added - ensures full width in flex container
`;

const ColumnRetentionLabel = styled.div`
  grid-column: 1 / 2 ;
`;

const ColumnRetentionValue = styled.div`
  grid-column: 2 / 2 ;
`;


const ConstraintTypeWrapper = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 90px 130px;
    grid-gap: 10px;
    grid-row: 1/1;
`;

const DurationWrapper = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 60px 140px 50px;
    grid-gap: 10px;
    grid-row: 1/1;
`;

const DurationWrapper1 = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 80px 80px;
    grid-gap: 10px;
    padding: 0px 0px 0px 0px;
`;

const DurationWrapper2 = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 80px 80px 80px;
    grid-gap: 10px;
    padding: 0px 0px 0px 0px;
`;

const OccurenceWrapper = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 60px 140px 50px;
    grid-gap: 10px;
    grid-row: 1/1;
    padding: 0px 20px 5px 15px; 
`;

const OccurenceWrapper1 = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 80px 80px;
    grid-gap: 10px;
    padding: 0px 0px 0px 0px;
`;

const OccurenceWrapper2 = styled.div`
    display: grid;
    align-items: baseline;
    grid-template-columns: 80px 80px 80px;
    grid-gap: 10px;
    padding: 0px 0px 0px 0px;
`;

const RadioComponent = styled(Radio)<{ checked?: boolean }>`
    color: ${({ checked }) =>
        checked
            ? getColor('lightBlue')
            : getColor('lightBorderColor')};

    & .MuiSvgIcon-root {
        width: 15px;
        height: 15px;
    }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
    margin-right: 30px;

    & .MuiFormControlLabel-label {
        color: ${getColor('inputTextColor')};
        font-family: ${getFontFamily('atlassian')};
        font-size: ${getFontSize('smaller')};
        letter-spacing: ${getLetterSpacing('small')};
        line-height: ${getLineHeight('small')};
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`;


const constraintTypeValues = [
    { value: 'na', label: 'N/A' },
    { value: 'duration', label: 'Duration' },
    { value: 'relative', label: 'Relative' },
    { value: 'occurences', label: 'Occurences' },
];

const duration_operators = [
    { value: 'greater than', label: 'Greater than' },
    { value: 'greater than or is', label: 'Greater than or is' },
    { value: 'less than', label: 'Less than' },
    { value: 'less than or is', label: 'Less than or is' },
    { value: 'equal to', label: 'Equal to' },
    { value: 'not equal to', label: 'Not equal to' },
    { value: 'between', label: 'Between' },
    { value: 'between or is', label: 'Between or is' },
    { value: 'outside', label: 'Outside' },
    { value: 'outside or is', label: 'Outside or is' },
];

const relative_operators = [
    { value: 'greater than avg duration', label: 'Greater than avg duration' },
    { value: 'less than avg duration', label: 'Less than avg duration' },
    { value: 'greater than avg duration + std deviation', label: 'Greater than avg duration + std deviation' },
    { value: 'Longest duration percentile', label: 'Longest duration percentile' },
    { value: 'Shortest duration percentile', label: 'Shortest duration percentile' },
];

const operatorValues = [
    { value: 'secs', label: 'secs' },
    { value: 'mins', label: 'mins' },
    { value: 'hrs', label: 'hrs' },
    { value: 'days', label: 'days' },
];


export const ActivityFilterTab = (mode) => {
    const [constraintType, setConstraintType] = React.useState<string>('na');
    const [durationOperator, setDurationOperator] = React.useState<string>('greater than');
    const [durationActualFrom, setDurationActualFrom] = React.useState<number>(0);
    const [durationActualTo, setDurationActualTo] = React.useState<number>(0);
    const [durationValue, setDurationValue] = React.useState<string>('hrs');
    const [occurenceOperator, setOccurenceOperator] = React.useState<string>('greater than');
    const [relativeOperator, setRelativeOperator] = React.useState<string>('greater than avg duration');
    const [occurenceActualFrom, setOccurenceActualFrom] = React.useState<number>(0);
    const [relativePercentile, setRelativePercentile] = React.useState<number>(0);
    const [occurenceActualTo, setOccurenceActualTo] = React.useState<number>(0);
    const sleep = ms => new Promise(r => setTimeout(r, ms));

    const [applyEnabled,setApplyEnabled] = React.useState<boolean>(true);

    const [retention, setRetention] = React.useState('include');

    const handleRetentionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectAction = (event.target as HTMLInputElement).value;
        setRetention(selectAction);
        apiProvider.changeActivitySelectRetention({ mode: selectAction });
    };

    const pane = 'Activities';
    async function onApply () {
        await openProgressModal({status: 'Processing Activity Filters'})
        await sleep(50)
        await apiProvider.applyActivityFilter({
            constraint_type: constraintType,
            duration_operator: durationOperator, 
            relative_operator: relativeOperator, 
            occurence_operator: occurenceOperator, 
            duration_actual_from: durationActualFrom, 
            duration_actual_to: durationActualTo, 
            occurence_actual_from: occurenceActualFrom, 
            occurence_actual_to: occurenceActualTo, 
            value: durationValue, 
            percentile: relativePercentile,
            mode: mode.mode})        
        setApplyEnabled(true)
        closeProgressModal()
    };

    const onConstraintTypeChange = (type: string) => {
        setConstraintType(type);
    };

    const onDurationValueChange = (value: string) => {
        setDurationValue(value);
    };

    const onDurationOperatorChange = (operator: string) => {
        setDurationOperator(operator);
    };

    const onRelativeOperatorChange = (operator: string) => {
        setRelativeOperator(operator);
    };

    const onOccurenceOperatorChange = (operator: string) => {
        setOccurenceOperator(operator);
    };

    React.useEffect(() => {
        setRetention('include')
        apiProvider.changeActivitySelectRetention({ mode: 'include' })
    }, []);

    return (
        <Wrapper>
            <SingleBlock>
                <ActivitiesGrid setApplyEnabled={setApplyEnabled} selectionMode='multiple'/>
            </SingleBlock>
            <GridOptions>
                <RowRetention>
                    <ColumnRetentionLabel>
                        <Section title='Retention'/>
                    </ColumnRetentionLabel>
                    <ColumnRetentionValue>
                    <RadioGroup
                            row
                            aria-label="selectAction"
                            name="selectAction"
                            value={retention}
                            onChange={handleRetentionChange}>
                            <StyledFormControlLabel
                                value="include"
                                control={<RadioComponent />}
                                label="Include" />
                            <StyledFormControlLabel
                                value="exclude"
                                control={<RadioComponent />}
                                label="Exclude" />
                        </RadioGroup>
                    </ColumnRetentionValue>
                </RowRetention> 

                { retention === 'include' ?
                <RowConstraints>
                    <ConstraintTypeWrapper>
                        <Section title='Constraint'/>
                        <Dropdown label='' value={constraintType} onChange={onConstraintTypeChange} options={constraintTypeValues} />   
                    </ConstraintTypeWrapper>

                    { constraintType === 'duration' ?
                    <DurationWrapper>
                        <Section title='Duration'/>
                        <Dropdown label='' value={durationOperator} onChange={onDurationOperatorChange} options={duration_operators} />   
                            { durationOperator === 'greater than' || 
                              durationOperator === 'greater than or is' ||
                              durationOperator === 'less than' ||
                              durationOperator === 'less than or is' ||
                              durationOperator === 'equal to' ||     
                              durationOperator === 'not equal to' ?     
                              <DurationWrapper1>
                                <NumberInput label='' value={durationActualFrom} min={0} onChange={setDurationActualFrom}/>
                                <Dropdown label='' value={durationValue} onChange={onDurationValueChange} options={operatorValues} />                       
                              </DurationWrapper1>
                            : '' }
                            { durationOperator === 'between' || 
                              durationOperator === 'between or is' ||
                              durationOperator === 'outside' ||
                              durationOperator === 'outside or is' ?     
                              <DurationWrapper2>
                                 <NumberInput label='' value={durationActualFrom} min={0} onChange={setDurationActualFrom}/>
                                 <NumberInput label='' value={durationActualTo} min={0} onChange={setDurationActualTo}/>
                                 <Dropdown label='' value={durationValue} onChange={onDurationValueChange} options={operatorValues} />         
                              </DurationWrapper2>
                            : '' }
                    </DurationWrapper>
                    : '' }
                    { constraintType === 'occurences' ?
                    <OccurenceWrapper>
                        <Section title='Occurence'/>
                        <Dropdown label='' value={occurenceOperator} onChange={onOccurenceOperatorChange} options={duration_operators} />   
                            { occurenceOperator === 'greater than' || 
                              occurenceOperator === 'greater than or is' ||
                              occurenceOperator === 'less than' ||
                              occurenceOperator === 'less than or is' ||
                              occurenceOperator === 'equal to' ||     
                              occurenceOperator === 'not equal to' ?     
                              <OccurenceWrapper1>
                                <NumberInput label='' value={occurenceActualFrom} min={0} onChange={setOccurenceActualFrom}/>
                              </OccurenceWrapper1>
                            : '' }
                            { occurenceOperator === 'between' || 
                              occurenceOperator === 'between or is' ||
                              occurenceOperator === 'outside' ||
                              occurenceOperator === 'outside or is' ?     
                              <OccurenceWrapper2>
                                 <NumberInput label='' value={occurenceActualFrom} min={0} onChange={setOccurenceActualFrom}/>
                                 <NumberInput label='' value={occurenceActualTo} min={0} onChange={setOccurenceActualTo}/>
                              </OccurenceWrapper2>
                            : '' }
                    </OccurenceWrapper>
                    : '' }            
                    { constraintType === 'relative' ?
                    <OccurenceWrapper>
                        <Section title='Duration'/>
                        <Dropdown label='' value={relativeOperator} onChange={onRelativeOperatorChange} options={relative_operators}  />   
                            { relativeOperator === 'Longest duration percentile' || 
                              relativeOperator === 'Shortest duration percentile'  ?     
                              <OccurenceWrapper1>
                                <NumberInput label='' value={relativePercentile} min={0} onChange={setRelativePercentile}/>
                              </OccurenceWrapper1>
                            : '' }
                    </OccurenceWrapper>
                    : '' }            
                </RowConstraints>
                : '' }

                <RowApply>
                    <Button label='Apply' onClick={onApply} disabled={applyEnabled}/>
                </RowApply>
            </GridOptions>
            <HelpText>Right mouse an activity for analysis options</HelpText>
            <AlertMessage/>
        </Wrapper>
    );
};